import React, { useState } from "react";
import { authApi } from "./api";
import {
  Box, Button, TextField, Typography, Stack, Collapse, Alert, InputAdornment, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";

export default function LoginRegister({ setUserState, notice }) {
  const [showRegister, setShowRegister] = useState(false);
  // Passwort vergessen
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  // Register State
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [gender, setGender] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regError, setRegError] = useState("");
  const [regSuccess, setRegSuccess] = useState("");
  // Login State
  const [logEmail, setLogEmail] = useState("");
  const [logPassword, setLogPassword] = useState("");
  const [logError, setLogError] = useState("");

  // LOGIN LOGIK
  const handleLogin = async (e) => {
    e.preventDefault(); setLogError("");
    try {
      const data = await authApi.login(logEmail, logPassword);
      setUserState(data.user || data);
    } catch (err) { setLogError(err.message); }
  };

  // PASSWORT VERGESSEN
  const handleForgot = async (e) => {
    e.preventDefault(); setForgotError(""); setForgotMessage(""); setForgotSending(true);
    try {
      const data = await authApi.forgotPassword(forgotEmail);
      setForgotMessage(data.message);
    } catch (err) { setForgotError(err.message); }
    finally { setForgotSending(false); }
  };

  // REGISTRIERUNGS LOGIK
  const handleRegister = async (e) => {
    e.preventDefault(); setRegError(""); setRegSuccess("");
    try {
      await authApi.register({ firstname, lastname, gender, email: regEmail, password: regPassword });
      setRegSuccess("Registrierung erfolgreich! Ein Admin muss dich noch freischalten.");
      setShowRegister(false); setFirstname(""); setLastname(""); setGender(""); setRegEmail(""); setRegPassword("");
    } catch (err) { setRegError(err.message); }
  };

  return (
    <Box sx={{ maxWidth: 460, mx: "auto", p: { xs: 2.5, sm: 4 }, borderRadius: 3, boxShadow: "0 18px 50px rgba(17,54,75,.14)", border: "1px solid rgba(18,79,123,.08)", borderTop: "5px solid #f3cf19", bgcolor: "#fff", mb: 3 }}>
      {/* Login immer sichtbar */}
      <Box component="form" onSubmit={handleLogin} sx={{ mb: 2 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography variant="h5">Willkommen zurück</Typography>
          <Typography color="text.secondary" sx={{ mt: .5, fontSize: 14 }}>Einloggen und direkt einen Platz reservieren.</Typography>
        </Box>
        {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="E-Mail Adresse"
            type="email"
            value={logEmail}
            onChange={e => setLogEmail(e.target.value)}
            fullWidth
            required
            InputProps={{ startAdornment: <InputAdornment position="start"><EmailRoundedIcon color="primary" fontSize="small" /></InputAdornment> }}
          />
          <TextField
            label="Passwort"
            type="password"
            value={logPassword}
            onChange={e => setLogPassword(e.target.value)}
            fullWidth
            required
            InputProps={{ startAdornment: <InputAdornment position="start"><LockRoundedIcon color="primary" fontSize="small" /></InputAdornment> }}
          />
          <Button type="submit" variant="contained" color="primary" fullWidth size="large" startIcon={<LoginRoundedIcon />}>
            Einloggen
          </Button>
          <Button variant="text" size="small" onClick={() => { setShowForgot(o => !o); setForgotEmail(f => f || logEmail); }} sx={{ alignSelf: "center" }}>
            Passwort vergessen?
          </Button>
        </Stack>
         {logError && <Alert severity="error" sx={{ mt: 2 }}>{logError}</Alert>}
      </Box>
      {/* Passwort vergessen (ausklappbar) */}
      <Collapse in={showForgot}>
        <Box component="form" onSubmit={handleForgot} sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: "rgba(18,79,123,.04)" }}>
          <Stack spacing={2}>
            <Typography variant="h6" align="center">Passwort zurücksetzen</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 14 }}>
              Gib deine E-Mail-Adresse ein. Du erhältst einen Link, mit dem du ein neues Passwort festlegen kannst.
            </Typography>
            <TextField
              label="E-Mail Adresse"
              type="email"
              value={forgotEmail}
              onChange={e => setForgotEmail(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <Button type="submit" variant="contained" fullWidth disabled={forgotSending}>
              {forgotSending ? "Wird gesendet …" : "Link anfordern"}
            </Button>
          </Stack>
          {forgotMessage && <Alert severity="success" sx={{ mt: 2 }}>{forgotMessage}</Alert>}
          {forgotError && <Alert severity="warning" sx={{ mt: 2 }}>{forgotError}</Alert>}
        </Box>
      </Collapse>
      {/* Button zum Ausklappen Registrieren */}
      {!showRegister && (
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography color="text.secondary" sx={{ fontSize: 13, mb: 1 }}>Noch kein Vereinskonto?</Typography>
          <Button variant="outlined" color="primary" startIcon={<PersonAddAltRoundedIcon />} onClick={() => setShowRegister(true)}>
            Jetzt registrieren
          </Button>
        </Box>
      )}
      {/* Registrierungsformular (ausklappbar) */}
      <Collapse in={showRegister}>
        <Box component="form" onSubmit={handleRegister} sx={{ mb: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6" align="center">
              Registrierung
            </Typography>
            <TextField
              label="Vorname"
              value={firstname}
              onChange={e => setFirstname(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Nachname"
              value={lastname}
              onChange={e => setLastname(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <FormControl fullWidth size="small" required>
              <InputLabel id="gender-label">Geschlecht</InputLabel>
              <Select labelId="gender-label" label="Geschlecht" value={gender} onChange={e => setGender(e.target.value)}>
                <MenuItem value="male">Männlich</MenuItem>
                <MenuItem value="female">Weiblich</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="E-Mail Adresse"
              type="email"
              value={regEmail}
              onChange={e => setRegEmail(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Passwort"
              type="password"
              value={regPassword}
              onChange={e => setRegPassword(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <Button type="submit" variant="contained" color="success" fullWidth>
              Registrierung abschließen
            </Button>
            <Button variant="text" color="primary" fullWidth onClick={() => setShowRegister(false)}>
              Zurück zum Login
            </Button>
          </Stack>
        </Box>
        {regError && <Alert severity="error" sx={{ mt: 2 }}>{regError}</Alert>}
        {regSuccess && <Alert severity="success" sx={{ mt: 2 }}>{regSuccess}</Alert>}
      </Collapse>
    </Box>
  );
}